import { useEffect, useState } from "react";
import { Check, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import WhatsAppLink from "@/components/admin/WhatsAppLink";

const MSG = (name) => `Hello ${name || ""}! Your child has been learning with Anou Appran. Could you give us 1 minute of feedback? ${window.location.origin}/survey — Bonjour ! Vous avez 1 minute pour nous donner votre avis ? ${window.location.origin}/survey`;
const day = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function SurveyInvites() {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/survey-candidates").then(({ data }) => setList(data.parents)).catch((e) => { setList([]); toast.error(formatError(e.response?.data?.detail) || e.message); });
  useEffect(() => { load(); }, []);
  const send = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/admin/survey-candidates/${p.id}/send`);
      if (data.email_sent) toast.success(`Survey sent to ${p.email}`); else toast.error("Banner shown in the app, but the email could not be sent");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };
  if (!list) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-yellow-300" /></div>;
  return (
    <div className="space-y-3" data-testid="admin-survey-invites">
      <p className="text-sm text-slate-400">Paying parents who joined more than 7 days ago and whose children use the programme. “Send survey” emails a feedback link and shows a “Give feedback” banner on their dashboard (never blocking).</p>
      {list.length === 0 && <p className="text-slate-400 text-center py-8" data-testid="survey-invites-empty">No paying parent with more than a week of use yet.</p>}
      {list.map((p) => (
        <div key={p.id} className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4 flex flex-wrap items-center gap-3" data-testid={`survey-invite-row-${p.id}`}>
          <div className="flex-1 min-w-[220px]">
            <p className="font-bold">{p.name} <span className="text-slate-400 font-normal text-sm">· {p.email}</span></p>
            <p className="text-xs text-slate-400 mt-0.5">{p.children.join(", ")} · joined {day(p.created_at)} · last payment {day(p.last_payment)} · {p.levels_played} levels played</p>
          </div>
          {p.survey_completed ? <span className="text-xs font-bold text-green-400 inline-flex items-center gap-1" data-testid={`survey-invite-done-${p.id}`}><Check size={13} /> Answered</span>
            : p.survey_requested_at ? <span className="text-xs text-yellow-300" data-testid={`survey-invite-sent-${p.id}`}>Sent {day(p.survey_requested_at)}</span> : null}
          <WhatsAppLink number={p.parent_whatsapp} name={p.parent_name} consentAccount={p.parent_consent_account} consentMarketing={p.parent_consent_marketing}
            message={MSG(p.name)} messageLabel="Survey" testId={`survey-invite-whatsapp-${p.id}`} />
          <button onClick={() => send(p)} disabled={busy === p.id} data-testid={`survey-invite-send-${p.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-yellow-300 text-slate-900 px-4 py-1.5 text-sm font-bold disabled:opacity-60">
            {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {p.survey_requested_at ? "Send again" : "Send survey"}
          </button>
        </div>
      ))}
    </div>
  );
}
